import { BaseGQLService } from '../../common/services/BaseGQLService';
import { RuntimeCacheService } from '../../common/services/RuntimeCacheService';
import { ModelType } from '../../common/utils/GQLIdEncoder';
import {
  CERTIFICATE_PERMISSIONS,
  GENERIC_PERMISSIONS,
  INTEGRATION_PERMISSIONS,
  PROFILE_PERMISSIONS,
  PROJECT_PERMISSIONS,
  Permission as PermissionEnum,
  REPORT_PERMISSIONS,
  SCAN_PERMISSIONS,
  SLOT_PERMISSIONS,
  USER_PERMISSIONS,
} from '../enum/Permission';

type Permission =
  | {
      granted: true;
      deniedReason: '';
    }
  | {
      granted: false;
      deniedReason: string;
    };

type Permissions<T extends string> = {
  [Prop in T]: Permission;
};

type BulkKey = {
  variableName: string;
  value: string;
};

/**
 * Reusable fragment in other queries. We ask for a `key`, and, if provided, we bind a `variable` to `variableName`.
 *
 * Example:
 *
 * 1. no-variable scenario
 *   createProject {
 *       granted
 *       deniedReason
 *   }
 *
 * 2. variable scenario
 *   updateProject(projectId: $projectId1) {
 *       granted
 *       deniedReason
 *   }
 *
 * We differentiate between `variableName` and the passed in `variable` to the query.
 */
const permissionQueryFragment = (key: string, variableName?: string, variable?: string) => `
  ${key}${variable && variableName ? `(${variableName}:$${variable})` : ''} {
    granted
    deniedReason
  }
`;

/**
 * Query for a single use-case scenario with a single variable passed in.
 *
 * Can be used to generic permissions, project permissions or any other scenario.
 */
export const PERMISSIONS_QUERY = (keys: readonly string[], variable?: string, variableType: string = 'ID!') => `
query${variable ? `($${variable}: ${variableType})` : ''}{
  viewer {
    permissions {
        ${keys.map((key) => permissionQueryFragment(key, variable, variable)).join('\n')}
    }
  }
}
`;

/**
 * Bulk permission fetching. Groups the multiple scenarios in one single.
 *
 * Using GQL aliases, it creates a query in the following format:
 *
 * query($permissions_1: ID!, $permissions_2: ID!, $permissions_3: ID!) {
 *     permissions_1: viewer {
 *         permissions {
 *             /* fragment permissions with bounded $permissions_1 variable
 *         }
 *     }
 *     permissions_2: viewer {
 *         permissions {
 *             /* fragment permissions with bounded $permissions_2 variable
 *         }
 *     }
 *     permissions_3: viewer {
 *         permissions {
 *             /* fragment permissions with bounded $permissions_3 variable
 *         }
 *     }
 * }
 *
 * This way we can fetch multiple permissions and store that if needed.
 *
 */
export const BULK_PERMISSIONS_QUERY = (keys: readonly string[], variableMap: Map<string, BulkKey>, variableType: string = 'ID!') => {
  const variables = [...variableMap.keys()];
  const queryVariables = variables.map((variable) => `$${variable}: ${variableType}`).join(', ');

  return `
    query(${queryVariables}) {
      ${variables
        .map(
          (variable) => `
        ${variable}: viewer {
          permissions {
            ${keys.map((key) => permissionQueryFragment(key, variableMap.get(variable)?.variableName, variable)).join('\n')}
          }
        }
      `
        )
        .join('\n')}
    }
  `;
};

export class PermissionsService extends BaseGQLService {
  private readonly runtimeCacheService = RuntimeCacheService.getInstance();

  async canCreateUser(): Promise<boolean> {
    return this.canInviteCoworker();
  }

  async canUpdateUser(userId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.User, userId);
    const permissions = await this.getUserPermissions(id);

    return [PermissionEnum.UPDATE_USER_NAME, PermissionEnum.UPDATE_USER_PERMISSIONS, PermissionEnum.UPDATE_USER_AUTH_TAGS].some((p) => permissions[p].granted);
  }

  async canUpdateUserPreferredName(userId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.User, userId);
    const permissions = await this.getUserPermissions(id);

    return permissions[PermissionEnum.UPDATE_USER_NAME].granted;
  }

  async canUpdateUserPermissions(userId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.User, userId);
    const permissions = await this.getUserPermissions(id);

    return permissions[PermissionEnum.UPDATE_USER_PERMISSIONS].granted;
  }

  async canDeleteUser(userId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.User, userId);
    const permissions = await this.getUserPermissions(id);

    return permissions[PermissionEnum.DELETE_USER].granted;
  }

  async canResetPassword(userId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.User, userId);
    const permissions = await this.getUserPermissions(id);

    return permissions[PermissionEnum.RESET_USER_PASSWORD].granted;
  }

  async canChangePassword(): Promise<boolean> {
    const permissions = await this.getGenericPermissions();

    return permissions[PermissionEnum.CHANGE_PASSWORD].granted;
  }

  async canCreateProject(): Promise<boolean> {
    const permissions = await this.getGenericPermissions();

    return permissions[PermissionEnum.CREATE_PROJECT].granted;
  }

  async canDownloadTracers(): Promise<boolean> {
    const permissions = await this.getGenericPermissions();

    return permissions[PermissionEnum.DOWNLOAD_TRACERS].granted;
  }

  async canInviteCoworker(): Promise<boolean> {
    const permissions = await this.getGenericPermissions();

    return permissions[PermissionEnum.CREATE_USER].granted;
  }

  async canAccessOrganizationCertificate(): Promise<boolean> {
    const permissions = await this.getGenericPermissions();

    return permissions[PermissionEnum.READ_ORGANIZATION_CERTIFICATES].granted;
  }

  async canAccessOrganizationKey(): Promise<boolean> {
    const permissions = await this.getGenericPermissions();

    return permissions[PermissionEnum.READ_ORGANIZATION_KEYS].granted;
  }

  async canAccessOrganizationUsers(): Promise<boolean> {
    const permissions = await this.getGenericPermissions();

    return permissions[PermissionEnum.READ_ORGANIZATION_USERS].granted;
  }

  async canAccessOrganizationScans(): Promise<boolean> {
    const permissions = await this.getGenericPermissions();

    return permissions[PermissionEnum.READ_ORGANIZATION_SCANS].granted;
  }

  async canUpdateProject(projectId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Project, projectId);
    const permissions = await this.getProjectPermissions(id);

    return permissions[PermissionEnum.UPDATE_PROJECT].granted;
  }

  async canDeleteProject(projectId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Project, projectId);
    const permissions = await this.getProjectPermissions(id);

    return permissions[PermissionEnum.DELETE_PROJECT].granted;
  }

  async canCreateSlot(projectId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Project, projectId);
    const permissions = await this.getProjectPermissions(id);

    return permissions[PermissionEnum.CREATE_SLOT].granted;
  }

  async canUploadTrace(projectId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Project, projectId);
    const permissions = await this.getProjectPermissions(id);

    return permissions[PermissionEnum.UPLOAD_TRACE].granted;
  }

  async canCreateException(projectId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Project, projectId);
    const permissions = await this.getProjectPermissions(id);

    return permissions[PermissionEnum.CREATE_EXCEPTIONS].granted;
  }

  async canUpdateSlot(slotId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Slot, slotId);
    const permissions = await this.getSlotPermissions(id);

    return permissions[PermissionEnum.UPDATE_SLOT].granted;
  }

  async canDeleteSlot(slotId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Slot, slotId);
    const permissions = await this.getSlotPermissions(id);

    return permissions[PermissionEnum.DELETE_SLOT].granted;
  }

  async canUpdateVenafiGuids(reportId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Report, reportId);
    const permissions = await this.getReportPermissions(id);

    return permissions[PermissionEnum.VENAFI_POPULATE_GUIDS].granted;
  }

  async canUpdateTanium(): Promise<boolean> {
    const permissions = await this.getIntegrationsPermissions();
    return permissions[PermissionEnum.TANIUM_EDIT_CONFIG].granted;
  }

  async canUpdateJira(): Promise<boolean> {
    const permissions = await this.getIntegrationsPermissions();
    return permissions[PermissionEnum.JIRA_EDIT_CONFIG].granted;
  }

  async canUpdateVenafi(): Promise<boolean> {
    const permissions = await this.getIntegrationsPermissions();
    return permissions[PermissionEnum.VENAFI_EDIT_CONFIG].granted;
  }

  async canUpdateServiceNow(): Promise<boolean> {
    const permission = await this.getIntegrationsPermissions();
    return permission[PermissionEnum.SERVICENOW_EDIT_CONFIG].granted;
  }

  async canReadCertificateVenafiDetails(certificateId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Certificate, certificateId);
    const permissions = await this.getCertificatePermissions(id);

    return permissions[PermissionEnum.VENAFI_CERTIFICATE_DETAILS].granted;
  }

  async canUploadCertificateToVenafi(certificateId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Certificate, certificateId);
    const permissions = await this.getCertificatePermissions(id);

    return permissions[PermissionEnum.VENAFI_CERTIFICATE_UPLOAD].granted;
  }

  async canUpdateCrowdStrike(): Promise<boolean> {
    const permissions = await this.getIntegrationsPermissions();
    return permissions[PermissionEnum.CROWDSTRIKE_EDIT_CONFIG].granted;
  }

  async canCreateProfile(): Promise<boolean> {
    const permissions = await this.getGenericPermissions();

    return permissions[PermissionEnum.CREATE_PROFILE].granted;
  }

  async canUpdateProfile(profileId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Profile, profileId);
    const permissions = await this.getProfilePermissions(id);

    return permissions[PermissionEnum.UPDATE_PROFILE].granted;
  }

  async canDeleteProfile(profileId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Profile, profileId);
    const permissions = await this.getProfilePermissions(id);

    return permissions[PermissionEnum.DELETE_PROFILE].granted;
  }

  async canCreateScan(): Promise<boolean> {
    const permissions = await this.getGenericPermissions();
    return permissions[PermissionEnum.CREATE_SCAN].granted;
  }

  async canUpdateScan(scanId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Scan, scanId);
    const permissions = await this.getScanPermissions(id);

    return permissions[PermissionEnum.UPDATE_SCAN].granted;
  }

  // TODO: apply this permission
  async canDeleteScan(scanId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Scan, scanId);
    const permissions = await this.getScanPermissions(id);

    return permissions[PermissionEnum.DELETE_SCAN].granted;
  }

  async canStartCrowdStrikeScan(): Promise<boolean> {
    const permissions = await this.getIntegrationsPermissions();
    return [
      permissions[PermissionEnum.CROWDSTRIKE_QUERY_HOSTS],
      permissions[PermissionEnum.CROWDSTRIKE_UPLOAD_FILES_STORAGE],
      permissions[PermissionEnum.CROWDSTRIKE_UPLOAD_SCRIPTS_STORAGE],
      permissions[PermissionEnum.CROWDSTRIKE_RUN_SCRIPTS_HOSTS],
      permissions[PermissionEnum.CROWDSTRIKE_UPLOAD_FILES_HOSTS],
      permissions[PermissionEnum.CROWDSTRIKE_READ_FILES_HOSTS],
    ].every((p) => p.granted);
  }

  async canUpdateProjectJira(projectId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Project, projectId);
    const permissions = await this.getProjectPermissions(id);
    return permissions[PermissionEnum.JIRA_EDIT_PROJECT_CONFIG].granted;
  }

  // TODO: apply this permission
  async canExportInstancesToJira(): Promise<boolean> {
    const permissions = await this.getIntegrationsPermissions();
    return permissions[PermissionEnum.JIRA_EXPORT_INSTANCES].granted;
  }

  // TODO: apply this permission
  async canCheckServiceNowConnection(): Promise<boolean> {
    const permission = await this.getIntegrationsPermissions();
    return permission[PermissionEnum.SERVICENOW_CHECK_CONNECTION].granted;
  }

  // TODO: apply this permission
  async canExportInstancesToServiceNow(): Promise<boolean> {
    const permissions = await this.getIntegrationsPermissions();
    return permissions[PermissionEnum.SERVICENOW_EXPORT_INSTANCES].granted;
  }

  async canUpdateProjectTanium(projectId: number): Promise<boolean> {
    const id = this.gqlIdEncoder.encode(ModelType.Project, projectId);
    const permissions = await this.getProjectPermissions(id);
    return permissions[PermissionEnum.TANIUM_EDIT_PROJECT_CONFIG].granted;
  }

  // TODO: apply this permission
  async canCheckTaniumConnection(): Promise<boolean> {
    const permission = await this.getIntegrationsPermissions();
    return permission[PermissionEnum.TANIUM_CHECK_CONNECTION].granted;
  }

  // TODO: apply this permission
  async canCheckVenafiConnection(): Promise<boolean> {
    const permission = await this.getIntegrationsPermissions();
    return permission[PermissionEnum.SERVICENOW_EDIT_CONFIG].granted;
  }

  /**
   * Prefetch project permissions.
   */
  async bulkPrefetchProjectPermissions(projectIds: number[]): Promise<void> {
    const keys = [...PROJECT_PERMISSIONS] as const;
    const ids = projectIds.map((projectId) => this.gqlIdEncoder.encode(ModelType.Project, projectId));

    return this.bulkPrefetchPermissions(keys, 'projectId', ids);
  }

  /**
   * Prefetch slot permissions.
   */
  async bulkPrefetchSlotPermissions(slotIds: number[]): Promise<void> {
    const keys = [...SLOT_PERMISSIONS] as const;
    const ids = slotIds.map((slotId) => this.gqlIdEncoder.encode(ModelType.Slot, slotId));

    return this.bulkPrefetchPermissions(keys, 'slotId', ids);
  }

  /**
   * Prefetch user permissions.
   */
  async bulkPrefetchUserPermissions(userIds: number[]): Promise<void> {
    const keys = [...USER_PERMISSIONS] as const;
    const ids = userIds.map((userId) => this.gqlIdEncoder.encode(ModelType.User, userId));

    return this.bulkPrefetchPermissions(keys, 'userId', ids);
  }

  /**
   * Prefetch profile permissions.
   */
  async bulkPrefetchProfilePermissions(profileIds: number[]): Promise<void> {
    const keys = [...PROFILE_PERMISSIONS] as const;
    const ids = profileIds.map((profileId) => this.gqlIdEncoder.encode(ModelType.Profile, profileId));

    return this.bulkPrefetchPermissions(keys, 'profileId', ids);
  }

  /**
   * Prefetch scan permissions.
   */
  async bulkPrefetchScanPermissions(scanIds: number[]): Promise<void> {
    const keys = [...SCAN_PERMISSIONS] as const;
    const ids = scanIds.map((scanId) => this.gqlIdEncoder.encode(ModelType.Scan, scanId));

    return this.bulkPrefetchPermissions(keys, 'scanId', ids);
  }

  /**
   * Allow fetching of multiple permissions in one bulk.
   * Useful for table displays where we have to fetch permissions for each row.
   *
   * Builds a query in following format:
   *
   * {
   *    permissions_0: viewer {
   *        permissions {...}
   *    }
   *    permissions_1: viewer {
   *        permissions {...}
   *    }
   *    permissions_2: viewer {
   *        permissions {...}
   *    }
   * }
   *
   * That way we do only a single query towards the API, but we prefetch all the values needed.
   * Same values are cached so they go through the same flow as we have with separate fetches.
   */
  private async bulkPrefetchPermissions<T extends string>(keys: readonly T[], variableName: string, ids: string[]): Promise<void> {
    const mappedKeys: BulkKey[] = ids.map((id) => ({ variableName, value: id }));

    const filteredKeys = mappedKeys.filter(({ variableName, value }) => {
      const key = this.createKey(keys, variableName, value);
      return !this.runtimeCacheService.hasItem(key);
    });

    if (filteredKeys.length === 0) {
      return;
    }

    const variableMap = filteredKeys.reduce((map, key, index) => {
      const variable = `permissions_${index}`;

      map.set(variable, key);
      return map;
    }, new Map<string, BulkKey>());

    const variables = filteredKeys.reduce((map: Record<string, string>, key, index) => {
      const variable = `permissions_${index}`;

      map[variable] = key.value;
      return map;
    }, {});

    await this.query(BULK_PERMISSIONS_QUERY(keys, variableMap), variables, (data) => {
      for (let i = 0; i < filteredKeys.length; i++) {
        const permissionKey = `permissions_${i}`;
        const variableKey = filteredKeys[i];

        const permissions = { ...data[permissionKey].permissions };
        for (const key of keys) {
          if (permissions[key] === null) {
            permissions[key] = this.getDefaultPermission();
          }
        }

        // save to cache
        const key = this.createKey(keys, variableKey.variableName, variableKey.value);
        this.runtimeCacheService.setItem(key, permissions, 60_000);
      }
    });
  }

  private getDefaultPermission(): Permission {
    return { granted: false, deniedReason: '' };
  }

  private createKey<T extends string>(keys: readonly T[], variable: string = '', variableValue: string = ''): string {
    return `permissions-service-${keys.join(',')}-${variable}-${variableValue}`;
  }

  private async getPermissions<T extends string>(
    keys: readonly T[],
    variable: string = '',
    variableValue: string = ''
  ): Promise<Permissions<(typeof keys)[number]>> {
    let variables = {};
    if (variable && variableValue) {
      variables = { [variable]: variableValue };
    }

    // check cache for the current key
    const key = this.createKey(keys, variable, variableValue);
    const cacheItem = this.runtimeCacheService.getItem<Permissions<(typeof keys)[number]>>(key);
    if (cacheItem !== null) {
      return cacheItem;
    }

    return await this.query(PERMISSIONS_QUERY(keys, variable), variables, (data) => {
      const permissions = { ...data.viewer.permissions };
      for (const key of keys) {
        if (permissions[key] === null) {
          permissions[key] = this.getDefaultPermission();
        }
      }

      // store to cache
      this.runtimeCacheService.setItem(key, permissions, 60_000);

      return permissions;
    });
  }

  private async getGenericPermissions() {
    return this.getPermissions([...GENERIC_PERMISSIONS] as const);
  }

  private async getProjectPermissions(projectId: string) {
    return this.getPermissions([...PROJECT_PERMISSIONS] as const, 'projectId', projectId);
  }

  private async getSlotPermissions(slotId: string) {
    return this.getPermissions([...SLOT_PERMISSIONS] as const, 'slotId', slotId);
  }

  private async getReportPermissions(reportId: string) {
    return this.getPermissions([...REPORT_PERMISSIONS] as const, 'reportId', reportId);
  }

  private async getIntegrationsPermissions() {
    return this.getPermissions([...INTEGRATION_PERMISSIONS] as const);
  }

  private async getCertificatePermissions(certificateId: string) {
    return this.getPermissions([...CERTIFICATE_PERMISSIONS] as const, 'certificateId', certificateId);
  }

  private async getUserPermissions(userId: string) {
    return this.getPermissions([...USER_PERMISSIONS] as const, 'userId', userId);
  }

  private async getProfilePermissions(profileId: string) {
    return this.getPermissions([...PROFILE_PERMISSIONS] as const, 'profileId', profileId);
  }

  private async getScanPermissions(scanId: string) {
    return this.getPermissions([...SCAN_PERMISSIONS] as const, 'scanId', scanId);
  }
}
